import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { statisticsService, DriverEarningsResponse } from '../../services/statisticsService';

type DateRangePreset = 'today' | 'last7days' | 'thisWeek' | 'thisMonth' | 'last30days';

interface DriverStatisticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  driverId: number;
  driverName: string;
}

const DriverStatisticsModal: React.FC<DriverStatisticsModalProps> = ({
  isOpen,
  onClose,
  driverId,
  driverName
}) => {
  const { t } = useTranslation();

  const [statisticsData, setStatisticsData] = useState<DriverEarningsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('last7days');

  const getDateRange = (preset: DateRangePreset): { from: string; to: string } => {
    const now = new Date();

    const getStartOfDay = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      const timezone = `${offsetSign}${offsetHours}:${offsetMinutes}`;

      return `${year}-${month}-${day}T00:00:00${timezone}`;
    };

    const getEndOfDay = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      const offset = -date.getTimezoneOffset();
      const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
      const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, '0');
      const offsetSign = offset >= 0 ? '+' : '-';
      const timezone = `${offsetSign}${offsetHours}:${offsetMinutes}`;

      return `${year}-${month}-${day}T23:59:59${timezone}`;
    };

    let fromDate: string;
    let toDate: string = getEndOfDay(now);

    switch (preset) {
      case 'today':
        fromDate = getStartOfDay(now);
        break;
      case 'last7days':
        const last7Start = new Date(now);
        last7Start.setDate(last7Start.getDate() - 6);
        fromDate = getStartOfDay(last7Start);
        break;
      case 'thisWeek':
        const dayOfWeek = now.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const monday = new Date(now);
        monday.setDate(monday.getDate() - daysFromMonday);
        fromDate = getStartOfDay(monday);
        break;
      case 'thisMonth':
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        fromDate = getStartOfDay(firstDay);
        break;
      case 'last30days':
        const last30Start = new Date(now);
        last30Start.setDate(last30Start.getDate() - 29);
        fromDate = getStartOfDay(last30Start);
        break;
    }

    return {
      from: fromDate,
      to: toDate
    };
  };

  const fetchStatistics = async (preset: DateRangePreset) => {
    setLoading(true);
    setError('');

    try {
      const dateRange = getDateRange(preset);
      const response = await statisticsService.getDriverEarnings({
        ...dateRange,
        driverId
      });

      if (response.success && response.data) {
        setStatisticsData(response.data);
      } else {
        setError(response.error || 'Failed to fetch driver statistics');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && driverId) {
      fetchStatistics(selectedPreset);
    }
  }, [isOpen, driverId, selectedPreset]);

  const handlePresetChange = (preset: DateRangePreset) => {
    setSelectedPreset(preset);
  };

  const formatPrice = (price: number): string => `${price.toLocaleString()} soum`;

  const getPresetLabel = (preset: DateRangePreset): string => {
    switch (preset) {
      case 'today':
        return t('statistics.today');
      case 'last7days':
        return t('statistics.last7days');
      case 'thisWeek':
        return t('statistics.thisWeek');
      case 'thisMonth':
        return t('statistics.thisMonth');
      case 'last30days':
        return t('statistics.last30days');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('statistics.driverStatistics')} - ${driverName}`}
      maxWidth="900px"
    >
      <div>
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            {(['today', 'last7days', 'thisWeek', 'thisMonth', 'last30days'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: `2px solid ${selectedPreset === preset ? '#17a2b8' : '#ddd'}`,
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  backgroundColor: selectedPreset === preset ? '#17a2b8' : '#fff',
                  color: selectedPreset === preset ? '#fff' : '#333',
                  fontWeight: selectedPreset === preset ? 600 : 500,
                  transition: 'all 0.2s ease',
                  opacity: loading ? 0.6 : 1,
                  fontSize: '0.95rem'
                }}
              >
                {getPresetLabel(preset)}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('statistics.loading')}
          </div>
        ) : !statisticsData ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('statistics.noData')}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            <div style={{
              backgroundColor: '#e7f3ff',
              borderLeft: '4px solid #007bff',
              padding: '0.75rem 1rem',
              borderRadius: '4px'
            }}>
              <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {t('statistics.ordersCount')}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#007bff' }}>
                {statisticsData.ordersCount}
              </div>
            </div>

            <div style={{
              backgroundColor: '#d4edda',
              borderLeft: '4px solid #28a745',
              padding: '0.75rem 1rem',
              borderRadius: '4px'
            }}>
              <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {t('statistics.totalDeliveryPrice')}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#28a745' }}>
                {formatPrice(statisticsData.totalDeliveryPrice)}
              </div>
            </div>

            <div style={{
              backgroundColor: '#fff3cd',
              borderLeft: '4px solid #ffc107',
              padding: '0.75rem 1rem',
              borderRadius: '4px'
            }}>
              <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {t('statistics.totalPlatformFee')}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#b8860b' }}>
                {formatPrice(statisticsData.totalPlatformFee)}
              </div>
            </div>

            <div style={{
              backgroundColor: '#d1ecf1',
              borderLeft: '4px solid #17a2b8',
              padding: '0.75rem 1rem',
              borderRadius: '4px'
            }}>
              <div style={{ color: '#666', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {t('statistics.totalDriverEarning')}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#17a2b8' }}>
                {formatPrice(statisticsData.totalDriverEarning)}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DriverStatisticsModal;
